import { User, UserRole } from 'shared/types/user';
import { mockDelay } from 'shared/services/api/mockDelay';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  name: string;
  email: string;
  password: string;
  role: UserRole;
  phone?: string;
  city?: string;
}

const SESSION_KEY = 'marco.session_user';

function generateId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function roleFromEmail(email: string): UserRole {
  if (email.startsWith('admin@')) return 'admin';
  if (email.startsWith('arena@')) return 'arena';
  if (email.startsWith('instrutor@')) return 'instrutor';
  if (email.startsWith('recepcao@')) return 'recepcao';
  return 'jogador';
}

// Personas fixas usadas pelo seletor de "modo apresentação", para navegar sem login real.
// A instrutora reaproveita "Prof. Camila Rocha" (instrutora da Turma B) e a recepção
// reaproveita "Bruno Lima" (já cadastrado como Recepção na equipe da arena) —
// modules/arena/class-groups e modules/arena/settings — pra manter os dados coerentes.
export const DEMO_USERS: Record<UserRole, User> = {
  jogador: { id: 'demo-jogador', name: 'Rafael Nunes', email: 'rafael@demo.com', role: 'jogador' },
  arena: { id: 'demo-arena', name: 'Ana Souza', email: 'ana@demo.com', role: 'arena' },
  admin: { id: 'demo-admin', name: 'Equipe Marcô', email: 'admin@demo.com', role: 'admin' },
  instrutor: { id: 'demo-instrutor', name: 'Prof. Camila Rocha', email: 'camila@demo.com', role: 'instrutor' },
  recepcao: { id: 'demo-recepcao', name: 'Bruno Lima', email: 'bruno@demo.com', role: 'recepcao' },
};

export function impersonate(role: UserRole): User {
  const user = DEMO_USERS[role];
  localStorage.setItem(SESSION_KEY, JSON.stringify(user));
  return user;
}

// Mock de autenticação para simular o fluxo real de login/cadastro antes da API existir.
// Quando a API de auth (seção 7 do PROJECT_FULL.MD) estiver pronta, troca-se o corpo
// destas funções por httpClient.post('/auth/login', ...) etc — a assinatura não muda.
export async function login({ email }: LoginCredentials): Promise<User> {
  const role = roleFromEmail(email);
  const user: User = await mockDelay(
    {
      id: generateId(),
      name: email.split('@')[0],
      email,
      role,
    },
    400
  );

  localStorage.setItem(SESSION_KEY, JSON.stringify(user));
  return user;
}

export async function register({ name, email, role, phone, city }: RegisterData): Promise<User> {
  const user: User = await mockDelay({ id: generateId(), name, email, role, phone, city }, 400);

  localStorage.setItem(SESSION_KEY, JSON.stringify(user));
  return user;
}

export async function logout(): Promise<void> {
  localStorage.removeItem(SESSION_KEY);
}

export function getStoredUser(): User | null {
  const raw = localStorage.getItem(SESSION_KEY);
  return raw ? (JSON.parse(raw) as User) : null;
}
